import React from 'react';
import SPAButton from '../shared/SPAButton';
import SPAPill from '../shared/SPAPill';
import FilterPill from './FilterPill';
import { matchesFilter } from './repairs';
import type { RepairFilter, RowRepair } from './repairs';
import type { AssignmentRow } from './useAssignmentRows';
import type { MapClass } from './useCourseMatch';

// AIDEV-NOTE: Counts that filter the table, not lists of their own. The per-assignment fixes live on
// the table's rows; a list here showed the same assignments twice. The one action kept here is the
// teacher-wide unassign, because it genuinely spans many assignments in one reviewed step.
const CHIPS: { filter: RepairFilter; label: string }[] = [
  { filter: 'needs', label: 'Needs action' },
  { filter: 'teacher', label: 'Teacher to fix' },
  { filter: 'attach', label: 'Courses to attach' },
  { filter: 'unassign', label: 'Classes to unassign' },
  { filter: 'take_column', label: 'Gradebook link on the wrong copy' },
  { filter: 'link', label: 'Never linked' },
  { filter: 'duplicate', label: 'Duplicates, nothing to do' },
];

interface RepairSummaryProps {
  assignments: AssignmentRow[];
  bulkBusy: boolean;
  classes: MapClass[];
  filter: RepairFilter;
  onBulk: () => void;
  onFilter: (filter: RepairFilter) => void;
  repairs: Map<number, RowRepair>;
}

const RepairSummary = ({
  assignments, bulkBusy, classes, filter, onBulk, onFilter, repairs,
}: RepairSummaryProps) => {
  const count = (chip: RepairFilter) => assignments
    .filter(row => matchesFilter(chip, repairs.get(row.assignment_id))).length;
  const noCourse = classes.filter(room => room.course_id == null);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <FilterPill active={filter === 'all'} onClick={() => onFilter('all')}>
          {`All · ${assignments.length}`}
        </FilterPill>
        {CHIPS.filter(chip => chip.filter === 'needs' || count(chip.filter) > 0).map(chip => (
          <FilterPill key={chip.filter} active={filter === chip.filter} onClick={() => onFilter(chip.filter)}>
            {`${chip.label} · ${count(chip.filter)}`}
          </FilterPill>
        ))}
      </div>

      {count('unassign') > 0 && (
        <div
          className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-rounded tw-border
            tw-border-solid tw-border-spa-border tw-bg-spa-bg-secondary tw-px-4 tw-py-3"
        >
          <div className="tw-max-w-3xl">
            <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
              Unassigning classes an assignment&apos;s LMS course does not hold stops the rejected posts.
              Nothing changes in the LMS, and a class is kept if any of its students has a score not yet
              delivered.
            </p>
            {noCourse.length > 0 && (
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                <span className="tw-text-sm tw-text-spa-tertiary">Classes in none of the LMS courses:</span>
                {noCourse.map(room => (
                  <SPAPill key={room.id} title={`Class ${room.id}`} tone="neutral">
                    {`${room.name ?? room.id} · ${room.students}`}
                  </SPAPill>
                ))}
              </div>
            )}
          </div>
          <SPAButton disabled={bulkBusy} size="xs" type="button" variant="primary" onClick={onBulk}>
            Review classes to unassign across all
          </SPAButton>
        </div>
      )}
    </div>
  );
};

export default RepairSummary;
